import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Alert,
  AlertTitle,
  LinearProgress,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  MenuItem,
  Stack
} from '@mui/material';
import {
  Person as PersonIcon,
  Place as PlaceIcon,
  Public as PublicIcon,
  Send as SendIcon,
  Preview as PreviewIcon
} from '@mui/icons-material';
import logger from '../../utils/logger';
import { LEAD_STATUSES } from '../../services/leadService';

const RADIUS_PRESETS = [10, 20, 30, 50, 75];

/**
 * Outreach email to outbound B2B leads (property-management companies).
 *
 * The driving use case is an understaffed technician: pick them, and target the
 * property managers whose offices sit within a radius of their home.
 */
const LeadOutreachDialog = ({ open, onClose }) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  // 'technician' | 'address' | 'all'
  const [audienceMode, setAudienceMode] = useState('technician');
  const [selectedTech, setSelectedTech] = useState(null);
  const [techInput, setTechInput] = useState('');
  const [techOptions, setTechOptions] = useState([]);
  const [techLoading, setTechLoading] = useState(false);
  const [address, setAddress] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(30);
  const [statusFilter, setStatusFilter] = useState('new');

  const [subject, setSubject] = useState('');
  const [heading, setHeading] = useState('');
  const [message, setMessage] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  // Company BCC mode: 'off' | 'first' | 'all' — matches ClientOutreachDialog.
  const [bccMode, setBccMode] = useState('first');

  const [recipients, setRecipients] = useState([]);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sendResult, setSendResult] = useState(null);

  const limitToArea = audienceMode !== 'all';

  useEffect(() => {
    if (audienceMode !== 'technician' || !open) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setTechLoading(true);
      try {
        const params = new URLSearchParams({
          activeOnly: 'true',
          limit: '20',
          ...(techInput.trim() ? { search: techInput.trim() } : {})
        });
        const response = await fetch(`${apiBase}/api/maids/light?${params}`, {
          headers: authHeaders()
        });
        const result = await response.json();
        if (!cancelled) setTechOptions(result.data || []);
      } catch {
        if (!cancelled) setTechOptions([]);
      } finally {
        if (!cancelled) setTechLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [audienceMode, techInput, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const targetPayload = () => {
    const statuses = statusFilter ? [statusFilter] : undefined;
    if (audienceMode === 'technician') {
      return { maidUserId: selectedTech?.userId, radiusMiles: Number(radiusMiles), statuses };
    }
    if (audienceMode === 'address') {
      return { address: address.trim(), radiusMiles: Number(radiusMiles), statuses };
    }
    return { statuses };
  };

  const targetProblem = () => {
    if (audienceMode === 'technician' && !selectedTech) return 'Pick a technician first.';
    if (audienceMode === 'address' && !address.trim()) return 'Enter an area address first.';
    return null;
  };

  const resetPreview = () => {
    setRecipients([]);
    setSelectedIds(new Set());
    setPreviewMeta(null);
    setSendResult(null);
  };

  const handlePreview = async () => {
    const problem = targetProblem();
    if (problem) {
      setError(problem);
      return;
    }

    setError('');
    setSuccess('');
    setPreviewing(true);
    resetPreview();

    try {
      const response = await fetch(`${apiBase}/api/notifications/leads/preview`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(targetPayload())
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Preview failed');

      setRecipients(data.data.recipients);
      setPreviewMeta(data.data);
      // Everyone in range is selected by default; deselect to trim.
      setSelectedIds(new Set(data.data.recipients.map((r) => r.id)));
      logger.log('[LeadOutreachDialog] Preview:', data.data.totalTargeted, 'targeted');
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      setError('Subject and message are required.');
      return;
    }
    if (selectedIds.size === 0) {
      setError('No recipients selected.');
      return;
    }
    if (
      !window.confirm(
        `Send this email to ${selectedIds.size} property manager${selectedIds.size === 1 ? '' : 's'}? ` +
          'This cannot be undone.'
      )
    ) {
      return;
    }

    setError('');
    setSuccess('');
    setSending(true);

    try {
      const response = await fetch(`${apiBase}/api/notifications/leads/send`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...targetPayload(),
          subject: subject.trim(),
          heading: heading.trim() || undefined,
          message: message.trim(),
          ctaText: ctaText.trim() || undefined,
          ctaUrl: ctaUrl.trim() || undefined,
          bccMode,
          includeLeadIds: [...selectedIds]
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Send failed');

      setSendResult(data.data);
      setSuccess(
        `Sent ${data.data.totalSent} of ${data.data.totalTargeted}` +
          (data.data.totalFailed ? `, ${data.data.totalFailed} failed` : '')
      );
      logger.log('[LeadOutreachDialog] Sent:', data.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    resetPreview();
    setError('');
    setSuccess('');
    onClose();
  };

  const toggleRecipient = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allSelected = recipients.length > 0 && recipients.every((r) => selectedIds.has(r.id));
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(recipients.map((r) => r.id)));

  const skipSummary = useMemo(() => {
    if (!previewMeta?.skippedByReason) return [];
    return Object.entries(previewMeta.skippedByReason);
  }, [previewMeta]);

  const busy = previewing || sending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <SendIcon color="primary" />
          Lead Outreach
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info">
              <AlertTitle>Targeting uses each company&apos;s office location</AlertTitle>
              We know where a property manager&apos;s <strong>office</strong> is, not where the
              properties they manage are — those are unknown until a lead converts. Office position
              is a good proxy for the area a PM works in, but not an exact match, so consider a
              wider radius than you would for customers.
            </Alert>
          </Grid>

          {/* ── Targeting ─────────────────────────────────────── */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                1. Who to reach
              </Typography>

              <ToggleButtonGroup
                value={audienceMode}
                exclusive
                size="small"
                onChange={(_e, value) => {
                  if (!value) return;
                  setAudienceMode(value);
                  resetPreview();
                }}
                sx={{ mb: 2, flexWrap: 'wrap' }}
              >
                <ToggleButton value="technician">
                  <PersonIcon fontSize="small" sx={{ mr: 0.5 }} /> Near a technician
                </ToggleButton>
                <ToggleButton value="address">
                  <PlaceIcon fontSize="small" sx={{ mr: 0.5 }} /> Near an address
                </ToggleButton>
                <ToggleButton value="all">
                  <PublicIcon fontSize="small" sx={{ mr: 0.5 }} /> All leads
                </ToggleButton>
              </ToggleButtonGroup>

              <Grid container spacing={2}>
                {audienceMode === 'technician' && (
                  <Grid item xs={12} sm={7}>
                    <Autocomplete
                      options={techOptions}
                      loading={techLoading}
                      value={selectedTech}
                      onChange={(_e, value) => {
                        setSelectedTech(value);
                        resetPreview();
                      }}
                      inputValue={techInput}
                      onInputChange={(_e, value) => setTechInput(value)}
                      getOptionLabel={(o) =>
                        `${o.firstName || ''} ${o.lastName || ''}`.trim() || o.email || ''
                      }
                      isOptionEqualToValue={(o, v) => o.userId === v.userId}
                      renderOption={(props, o) => (
                        <li {...props} key={o.userId}>
                          <Box>
                            {`${o.firstName || ''} ${o.lastName || ''}`.trim()}
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {typeof o.residentialAddress === 'string'
                                ? o.residentialAddress
                                : o.residentialAddress
                                  ? [o.residentialAddress.street, o.residentialAddress.city]
                                      .filter(Boolean)
                                      .join(', ')
                                  : 'No address on file'}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Understaffed technician"
                          size="small"
                          helperText="Their home address becomes the centre of the radius"
                        />
                      )}
                    />
                  </Grid>
                )}

                {audienceMode === 'address' && (
                  <Grid item xs={12} sm={7}>
                    <TextField
                      label="Area address"
                      value={address}
                      onChange={(event) => {
                        setAddress(event.target.value);
                        resetPreview();
                      }}
                      size="small"
                      fullWidth
                      helperText="e.g. 924 N Magnolia Ave, Orlando, FL"
                    />
                  </Grid>
                )}

                {limitToArea && (
                  <Grid item xs={6} sm={2}>
                    <TextField
                      select
                      label="Radius"
                      value={radiusMiles}
                      onChange={(event) => {
                        setRadiusMiles(event.target.value);
                        resetPreview();
                      }}
                      size="small"
                      fullWidth
                    >
                      {RADIUS_PRESETS.map((miles) => (
                        <MenuItem key={miles} value={miles}>
                          {miles} miles
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={6} sm={3}>
                  <TextField
                    select
                    label="Pipeline status"
                    value={statusFilter}
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      resetPreview();
                    }}
                    size="small"
                    fullWidth
                    helperText="Usually 'new'"
                  >
                    <MenuItem value="">Any status</MenuItem>
                    {LEAD_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<PreviewIcon />}
                    onClick={handlePreview}
                    disabled={busy}
                  >
                    Preview recipients
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {busy && (
            <Grid item xs={12}>
              <LinearProgress />
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                {sending ? 'Sending — this paces at about one email every 3 seconds…' : 'Loading…'}
              </Typography>
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          {/* ── Recipients ────────────────────────────────────── */}
          {previewMeta && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                  <Typography variant="h6">2. Recipients</Typography>
                  <Chip label={`${recipients.length} reachable`} color="success" size="small" />
                  <Chip label={`${selectedIds.size} selected`} color="primary" size="small" />
                  {previewMeta.areaLabel && (
                    <Chip label={previewMeta.areaLabel} size="small" variant="outlined" />
                  )}
                </Box>

                {skipSummary.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Excluded:{' '}
                    {skipSummary.map(([reason, count], index) => (
                      <span key={reason}>
                        {index > 0 && ' · '}
                        {count} {reason}
                      </span>
                    ))}
                  </Typography>
                )}

                {recipients.length === 0 ? (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    No reachable leads matched. Try a wider radius, or set status to “Any”.
                  </Alert>
                ) : (
                  <>
                    <Button size="small" onClick={toggleAll}>
                      {allSelected ? 'Deselect all' : 'Select all'}
                    </Button>
                    <List dense sx={{ maxHeight: 260, overflowY: 'auto' }}>
                      {recipients.map((recipient) => (
                        <ListItem key={recipient.id} disablePadding>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              size="small"
                              checked={selectedIds.has(recipient.id)}
                              onChange={() => toggleRecipient(recipient.id)}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={recipient.companyName}
                            secondary={`${recipient.email}${
                              recipient.distance != null ? ` · ${recipient.distance} mi` : ''
                            }${recipient.city ? ` · ${recipient.city}` : ''}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}
              </Paper>
            </Grid>
          )}

          {/* ── Compose ───────────────────────────────────────── */}
          {previewMeta && recipients.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  3. Compose
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Use <code>[COMPANY_NAME]</code> anywhere in the subject, heading or message and it
                  will be replaced with each company&apos;s name.
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    label="Subject"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    fullWidth
                    size="small"
                    required
                  />
                  <TextField
                    label="Heading (optional — defaults to the subject)"
                    value={heading}
                    onChange={(event) => setHeading(event.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Message"
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    fullWidth
                    multiline
                    minRows={6}
                    required
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        label="Button text (optional)"
                        value={ctaText}
                        onChange={(event) => setCtaText(event.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <TextField
                        label="Button link (optional)"
                        value={ctaUrl}
                        onChange={(event) => setCtaUrl(event.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" sx={{ mb: 1 }}>
                  Send a copy to the company inbox (BCC)
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={bccMode}
                  onChange={(_e, mode) => {
                    if (mode) setBccMode(mode);
                  }}
                  sx={{ mb: 2, flexWrap: 'wrap' }}
                >
                  <ToggleButton value="off">No BCC</ToggleButton>
                  <ToggleButton value="first">First email only</ToggleButton>
                  <ToggleButton value="all">Every email</ToggleButton>
                </ToggleButtonGroup>

                <Divider sx={{ mb: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Recipients are marked “contacted” after a successful send. Anyone who replies
                  asking to stop should be unsubscribed from the Property Manager Leads page — they
                  are then excluded from every future campaign.
                </Typography>
              </Paper>
            </Grid>
          )}

          {sendResult?.results?.some((r) => !r.success) && (
            <Grid item xs={12}>
              <Alert severity="warning">
                <AlertTitle>Some sends failed</AlertTitle>
                {sendResult.results
                  .filter((r) => !r.success)
                  .slice(0, 10)
                  .map((r) => (
                    <div key={r.leadId}>
                      {r.companyName}: {r.error || 'unknown error'}
                    </div>
                  ))}
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={sending}>
          Close
        </Button>
        <Button
          variant="contained"
          startIcon={<SendIcon />}
          onClick={handleSend}
          disabled={busy || selectedIds.size === 0 || !subject.trim() || !message.trim()}
        >
          Send to {selectedIds.size}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadOutreachDialog;
